import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../admin/firebase/config';
import { useDeliveryAuth } from '../context/DeliveryAuthContext';
import './DeliveryProfile.css';

const DeliveryProfile = () => {
  const { deliveryBoyData, logout } = useDeliveryAuth();
  const [stats, setStats] = useState({
    totalDeliveries: 0,
    completedDeliveries: 0,
    cancelledDeliveries: 0,
    todayDeliveries: 0,
    weekDeliveries: 0,
    monthDeliveries: 0,
    totalEarnings: 0,
    todayEarnings: 0,
    weekEarnings: 0,
    monthEarnings: 0,
    avgRating: 0,
    totalRatings: 0
  });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (!deliveryBoyData) return;

    const fetchStats = async () => {
      try {
        const ordersQuery = query(
          collection(db, 'orders'),
          where('deliveryBoy.id', '==', deliveryBoyData.id)
        );

        const snapshot = await getDocs(ordersQuery);
        const orders = snapshot.docs.map(doc => doc.data());

        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

        // Calculate statistics
        const totalDeliveries = orders.length;
        const completedDeliveries = orders.filter(o => o.status === 'delivered').length;
        const cancelledDeliveries = orders.filter(o => o.status === 'cancelled').length;

        const todayOrders = orders.filter(o => {
          const orderDate = o.createdAt?.toDate();
          return orderDate >= today;
        });

        const weekOrders = orders.filter(o => {
          const orderDate = o.createdAt?.toDate();
          return orderDate >= weekAgo;
        });

        const monthOrders = orders.filter(o => {
          const orderDate = o.createdAt?.toDate();
          return orderDate >= monthAgo;
        });

        const completedOrders = orders.filter(o => o.status === 'delivered');
        const totalEarnings = completedOrders.reduce((sum, o) => sum + (o.deliveryCharge || 30), 0);

        const todayCompleted = todayOrders.filter(o => o.status === 'delivered');
        const todayEarnings = todayCompleted.reduce((sum, o) => sum + (o.deliveryCharge || 30), 0);

        const weekCompleted = weekOrders.filter(o => o.status === 'delivered');
        const weekEarnings = weekCompleted.reduce((sum, o) => sum + (o.deliveryCharge || 30), 0);

        const monthCompleted = monthOrders.filter(o => o.status === 'delivered');
        const monthEarnings = monthCompleted.reduce((sum, o) => sum + (o.deliveryCharge || 30), 0);

        // Calculate ratings (if ratings exist)
        const ordersWithRatings = orders.filter(o => o.deliveryRating);
        const totalRatings = ordersWithRatings.length;
        const avgRating = totalRatings > 0
          ? ordersWithRatings.reduce((sum, o) => sum + o.deliveryRating, 0) / totalRatings
          : 0;

        setStats({
          totalDeliveries,
          completedDeliveries,
          cancelledDeliveries,
          todayDeliveries: todayOrders.length,
          weekDeliveries: weekOrders.length,
          monthDeliveries: monthOrders.length,
          totalEarnings,
          todayEarnings,
          weekEarnings,
          monthEarnings,
          avgRating: avgRating.toFixed(1),
          totalRatings
        });

        setLoading(false);
      } catch (error) {
        console.error('Error fetching stats:', error);
        setLoading(false);
      }
    };

    fetchStats();
  }, [deliveryBoyData]);

  const handleLogout = async () => {
    if (window.confirm('Are you sure you want to logout?')) {
      await logout();
      navigate('/delivery/login');
    }
  };

  const successRate = stats.totalDeliveries > 0
    ? ((stats.completedDeliveries / stats.totalDeliveries) * 100).toFixed(1)
    : 0;

  if (loading) {
    return (
      <div className="delivery-profile">
        <div className="loading-spinner">Loading profile...</div>
      </div>
    );
  }

  return (
    <div className="delivery-profile">
      {/* Header */}
      <div className="profile-header">
        <button onClick={() => navigate('/delivery/dashboard')} className="back-icon-btn">
          ← Back
        </button>
        <h1>My Profile</h1>
        <div></div>
      </div>

      {/* Profile Card */}
      <div className="profile-content">
        <div className="profile-info-card">
          <div className="profile-avatar">
            <div className="avatar-circle">
              {deliveryBoyData?.name?.charAt(0).toUpperCase() || 'D'}
            </div>
          </div>
          <h2>{deliveryBoyData?.name || 'Delivery Boy'}</h2>
          <p className="profile-email">{deliveryBoyData?.email}</p>
          <p className="profile-role">Delivery Partner</p>
          {stats.avgRating > 0 && (
            <div className="rating-display">
              <span className="stars">⭐ {stats.avgRating}</span>
              <span className="rating-count">({stats.totalRatings} ratings)</span>
            </div>
          )}
        </div>

        {/* Earnings Overview */}
        <div className="section-card">
          <h3>💰 Earnings Overview</h3>
          <div className="earnings-grid">
            <div className="earning-item">
              <span className="earning-label">Today</span>
              <span className="earning-value">₹{stats.todayEarnings}</span>
            </div>
            <div className="earning-item">
              <span className="earning-label">This Week</span>
              <span className="earning-value">₹{stats.weekEarnings}</span>
            </div>
            <div className="earning-item">
              <span className="earning-label">This Month</span>
              <span className="earning-value">₹{stats.monthEarnings}</span>
            </div>
            <div className="earning-item total">
              <span className="earning-label">Total Earnings</span>
              <span className="earning-value">₹{stats.totalEarnings}</span>
            </div>
          </div>
        </div>

        {/* Delivery Stats */}
        <div className="section-card">
          <h3>📊 Delivery Statistics</h3>
          <div className="stats-grid-profile">
            <div className="stat-item">
              <div className="stat-icon">📦</div>
              <div className="stat-details">
                <div className="stat-number">{stats.totalDeliveries}</div>
                <div className="stat-text">Total Deliveries</div>
              </div>
            </div>
            <div className="stat-item">
              <div className="stat-icon">✅</div>
              <div className="stat-details">
                <div className="stat-number">{stats.completedDeliveries}</div>
                <div className="stat-text">Completed</div>
              </div>
            </div>
            <div className="stat-item">
              <div className="stat-icon">❌</div>
              <div className="stat-details">
                <div className="stat-number">{stats.cancelledDeliveries}</div>
                <div className="stat-text">Cancelled</div>
              </div>
            </div>
            <div className="stat-item">
              <div className="stat-icon">📈</div>
              <div className="stat-details">
                <div className="stat-number">{successRate}%</div>
                <div className="stat-text">Success Rate</div>
              </div>
            </div>
          </div>
        </div>

        {/* Period Stats */}
        <div className="section-card">
          <h3>📅 Deliveries by Period</h3>
          <div className="period-stats">
            <div className="period-item">
              <span className="period-label">Today</span>
              <span className="period-value">{stats.todayDeliveries} deliveries</span>
            </div>
            <div className="period-item">
              <span className="period-label">This Week</span>
              <span className="period-value">{stats.weekDeliveries} deliveries</span>
            </div>
            <div className="period-item">
              <span className="period-label">This Month</span>
              <span className="period-value">{stats.monthDeliveries} deliveries</span>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="section-card">
          <h3>⚡ Quick Actions</h3>
          <div className="quick-actions">
            <button
              className="action-button"
              onClick={() => navigate('/delivery/history')}
            >
              <span className="action-icon">📋</span>
              View Delivery History
            </button>
            <button
              className="action-button"
              onClick={() => navigate('/delivery/dashboard')}
            >
              <span className="action-icon">🏠</span>
              Go to Dashboard
            </button>
            <button
              className="action-button logout"
              onClick={handleLogout}
            >
              <span className="action-icon">🚪</span>
              Logout
            </button>
          </div>
        </div>

        {/* Contact Info */}
        <div className="section-card">
          <h3>📞 Contact Information</h3>
          <div className="contact-info">
            {deliveryBoyData?.phoneNumber && (
              <div className="contact-item">
                <span className="contact-label">Phone:</span>
                <span className="contact-value">{deliveryBoyData.phoneNumber}</span>
              </div>
            )}
            <div className="contact-item">
              <span className="contact-label">Email:</span>
              <span className="contact-value">{deliveryBoyData?.email}</span>
            </div>
            <div className="contact-item">
              <span className="contact-label">Account Status:</span>
              <span className="contact-value status-active">Active</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeliveryProfile;
